/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unreachable */
import { api } from '../utils/api';
import { usePlan } from '../hooks/usePlan';
import React, { useState, useEffect, useRef } from 'react';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    RadioGroup,
    Radio,
    Divider,
    Avatar,
    IconButton,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    useTheme,
    AppBar,
    Toolbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    CircularProgress,
    Checkbox,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormGroup,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';
import {
    ArrowBack,
    Lock,
    People,
    Public,
    Person,
    Notifications,
    Security,
    PhotoCamera,
    Save,
    Delete,
    Visibility,
    VisibilityOff,
    Warning,
    ExpandMore,
    Flight,
    Hotel,
    Phone,
    Email,
    Sms,
    Badge,
    AirlineSeatReclineExtra,
    LocalHospital,
    DirectionsCar,
    LocalTaxi,
    DirectionsBike,
    Train,
    Accessible,
    School,
    MilitaryTech,
    ElderlyWoman,
    VerifiedUser,
    SupportAgent,
    Link as LinkIcon,
} from '@mui/icons-material';


// Tab index → URL hash slug. Order matches the <Tab> render order below.
// Updating this list requires updating the <Tab> ordering at L779-786 to match.
const SETTINGS_TABS = [
  'privacy',
  'profile',
  'flights',
  'hotels',
  'transport',
  'access',
  'notifications',
  'account',
  'support',
] as const;

function tabIndexFromHash(): number {
  if (typeof window === 'undefined') return 0;
  const hash = window.location.hash.replace(/^#/, '').toLowerCase();
  const idx = SETTINGS_TABS.indexOf(hash as any);
  return idx >= 0 ? idx : 0;
}

// #230 Phase 4: unified preferences interface backed by Phase 5.7 endpoint
// (handler extended in Patch 2 to handle all 17 fields). Replaces the
// previous parallel CategoryNotificationPreferences and nested-shape
// NotificationPreferences interfaces.
interface NotificationPreferences {
  // Lifecycle / broadcast categories (formerly CategoryNotificationPreferences)
  featureAnnouncementsEnabled: boolean;
  marketingEnabled: boolean;
  travelRemindersEnabled: boolean;
  travelReminderIntervals: number[];
  // Per-trip in-app nudges (formerly nested .app.*)
  appNudgesEnabled: boolean;
  appPhotos: boolean;
  appShare: boolean;
  appBookings: boolean;
  appFriends: boolean;
  // Email digest (formerly nested .email.*)
  emailEnabled: boolean;
  emailPhotos: boolean;
  emailShare: boolean;
  emailBookings: boolean;
  emailFriends: boolean;
  emailFrequency: 'immediately' | 'daily' | 'weekly';
}

interface AIPreferences {
  trip_planner: boolean;
  activity_recommendations: boolean;
  trip_import: boolean;
  photo_analysis: boolean;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

interface EmergencyContact {
    name: string;
    relationship: string;
    phone: string;
    email: string;
}

interface UserSettings {
    // Profile
    displayName: string;
    email: string;
    bio: string;
    avatarUrl: string;
    phone: string;
    preferredContact: 'email' | 'sms' | 'phone';
    
    // Passport & Travel Docs
    passportCountry: string;
    passportNumber: string;
    passportExpiry: string;
    tsaPrecheck: string;
    globalEntry: string;
    knownTravelerNumber: string;
    
    // Legal Name (as on ID/Passport) - for checkout auto-population
    legalFirstName: string;
    legalMiddleName: string;
    legalLastName: string;
    dateOfBirth: string;
    gender: 'M' | 'F' | 'X' | '';

    // Emergency Contact
    emergencyContact: EmergencyContact;
    
    // Airline Preferences
    preferredAirline: string;
    frequentFlyerNumber: string;
    seatPreference: 'window' | 'middle' | 'aisle' | 'no_preference';
    mealPreference: string;
    specialAssistance: string;
    
    // Hotel Preferences
    preferredHotel: string;
    hotelLoyaltyNumber: string;
    roomPreference: string;
    bedPreference: 'king' | 'queen' | 'double' | 'twin' | 'no_preference';
    floorPreference: 'high' | 'low' | 'no_preference';
    
    // Transportation
    preferredRideShare: string;
    uberAccountLinked: boolean;
    uberEmail: string;
    lyftAccountLinked: boolean;
    lyftEmail: string;
    preferredCarRental: string;
    carRentalLoyaltyNumber: string;
    preferredCarType: string;
    hasTransitPass: boolean;
    transitPassType: string;
    preferredBikeShare: string;
    
    // Accessibility
    requiresWheelchair: boolean;
    requiresMobilityAssistance: boolean;
    requiresVisualAssistance: boolean;
    requiresHearingAssistance: boolean;
    hasServiceAnimal: boolean;
    serviceAnimalType: string;
    medicalDietaryNeeds: string;
    tsaCaresEnrolled: boolean;
    otherAccessibilityNeeds: string;
    
    // Special Status
    isStudent: boolean;
    studentEmail: string;
    studentIdNumber: string;
    isVeteran: boolean;
    veteranIdNumber: string;
    militaryBranch: string;
    isSenior: boolean;
    seniorAge: string;
    isAAAMember: boolean;
    aaaMemberNumber: string;
    isGovernmentEmployee: boolean;
    governmentAgency: string;
    isFirstResponder: boolean;
    firstResponderType: string;
    
    // Privacy
    defaultTripVisibility: 'private' | 'friends_family' | 'public';
    profileVisibility: 'private' | 'friends_family' | 'public';
    showInCommunity: boolean;
    allowFriendRequests: boolean;
    
    // Auto-share settings
    autoSharePhotos: boolean;
    autoShareItinerary: boolean;
    autoShareDates: boolean;
    autoShareDestination: boolean;
    autoShareName: boolean;
    
    // Privacy acknowledgements
    friendsFamilyAcknowledged: boolean;
    publicAcknowledged: boolean;
    
    // Notifications
    emailNotifications: boolean;
    tripReminders: boolean;
    marketingEmails: boolean;
    friendActivity: boolean;
    creditAlerts: boolean;
}

// ── SupportSettingsPanel — user's own tickets + new ticket form ──────────────
const SupportSettingsPanel: React.FC = () => {
    const [tickets, setTickets] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [formOpen, setFormOpen] = React.useState(false);
    const [subject, setSubject] = React.useState('');
    const [message, setMessage] = React.useState('');
    const [category, setCategory] = React.useState('');
    const [priority, setPriority] = React.useState('normal');
    const [submitting, setSubmitting] = React.useState(false);
    const [submitSuccess, setSubmitSuccess] = React.useState(false);
    const [submitError, setSubmitError] = React.useState('');

    const { apiRequest } = require('../utils/api');

    const loadTickets = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.get('/support/tickets');
            const data = await res.json();
            if (data.success) setTickets(data.tickets);
            else setError(data.error || 'Failed to load tickets');
        } catch {
            setError('Network error loading tickets');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    React.useEffect(() => { loadTickets(); }, [loadTickets]);

    const submitTicket = async () => {
        if (!subject.trim() || !message.trim()) return;
        setSubmitting(true);
        setSubmitError('');
        try {
            const res = await apiRequest('/support/tickets', {
                method: 'POST',
                body: JSON.stringify({ subject: subject.trim(), message: message.trim(), category: category || undefined, priority }),
            });
            const data = await res.json();
            if (data.success) {
                setSubmitSuccess(true);
                setFormOpen(false);
                setSubject(''); setMessage(''); setCategory(''); setPriority('normal');
                await loadTickets();
            } else {
                setSubmitError(data.error || 'Failed to submit ticket');
            }
        } catch {
            setSubmitError('Network error');
        } finally {
            setSubmitting(false);
        }
    };

    const statusColor = (s: string) => {
        if (s === 'resolved' || s === 'closed') return 'success';
        if (s === 'pending') return 'warning';
        return 'default';
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h6" fontWeight={600}>Support</Typography>
                    <Typography variant="body2" color="text.secondary">View your support requests or contact our team</Typography>
                </Box>
                <Button variant="contained" onClick={() => setFormOpen(true)}
                    sx={{ backgroundColor: '#2D6E6A', '&:hover': { backgroundColor: '#009688' } }}>
                    New Request
                </Button>
            </Box>

            {submitSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSubmitSuccess(false)}>Your support request has been submitted. We'll be in touch shortly.</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : tickets.length === 0 ? (
                <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary" gutterBottom>No support requests yet</Typography>
                    <Typography variant="body2" color="text.secondary">Have a question or issue? Submit a request and we'll get back to you.</Typography>
                </Card>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell><strong>Subject</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Priority</strong></TableCell>
                            <TableCell><strong>Submitted</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tickets.map((t: any) => (
                            <TableRow key={t.ticket_id}>
                                <TableCell><Typography variant="body2">{t.subject}</Typography></TableCell>
                                <TableCell><Chip label={t.status} color={statusColor(t.status) as any} size="small" /></TableCell>
                                <TableCell><Chip label={t.priority} size="small" /></TableCell>
                                <TableCell><Typography variant="caption">{new Date(t.created_at).toLocaleDateString()}</Typography></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New Support Request</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <TextField fullWidth size="small" label="Subject" value={subject}
                        onChange={e => setSubject(e.target.value)} sx={{ mb: 2, mt: 1 }} />
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Category</InputLabel>
                        <Select value={category} label="Category" onChange={e => setCategory(e.target.value)}>
                            <MenuItem value="">Select a category</MenuItem>
                            <MenuItem value="bug">Bug / Something broken</MenuItem>
                            <MenuItem value="billing">Billing</MenuItem>
                            <MenuItem value="account">Account</MenuItem>
                            <MenuItem value="trip">Trip</MenuItem>
                            <MenuItem value="feature_request">Feature Request</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Priority</InputLabel>
                        <Select value={priority} label="Priority" onChange={e => setPriority(e.target.value)}>
                            <MenuItem value="low">Low</MenuItem>
                            <MenuItem value="normal">Normal</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                            <MenuItem value="urgent">Urgent</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField fullWidth multiline rows={5} size="small" label="Describe your issue"
                        value={message} onChange={e => setMessage(e.target.value)} />
                    {submitError && <Alert severity="error" sx={{ mt: 1 }}>{submitError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFormOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={submitTicket}
                        disabled={submitting || !subject.trim() || !message.trim()}
                        sx={{ backgroundColor: '#2D6E6A' }}>
                        {submitting ? 'Submitting...' : 'Submit Request'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const SettingsPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
  const location = useLocation();

  // Auto-open section when navigated with state (e.g. from Header Contact Support)
  React.useEffect(() => {
    const state = location.state as { openSection?: string } | null;
    if (state?.openSection) {
      setActiveSection(state.openSection);
      // Clear state so back-navigation doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
    const { isEnabled } = useFeatureFlags();
    const { plan } = usePlan();
    const tierLabel = plan.toUpperCase();
    const tierDescription = plan === 'pro' ? 'Pro plan — 15 GB storage, companions & more' : plan === 'premium' ? 'Premium plan — 50 GB storage, unlimited companions' : 'Free tier — unlimited trips and photos';
    const masterPublicEnabled = isEnabled('master_public');
    const masterFriendsEnabled = isEnabled('master_friends');
    const publicTripsEnabled = masterPublicEnabled && isEnabled('public_trips');
    const friendsTripsEnabled = masterFriendsEnabled && isEnabled('friends_trips');
    const bookingEnabled = isEnabled('booking_enabled');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [tabValue, setTabValue] = useState(() => tabIndexFromHash());
    const [idCopied, setIdCopied] = useState(false);
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [error, setError] = useState('');
    
    // Password change dialog
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    
    // Delete account dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    
    // Acknowledgement dialogs
    const [friendsFamilyDialogOpen, setFriendsFamilyDialogOpen] = useState(false);
    const [publicDialogOpen, setPublicDialogOpen] = useState(false);
    const [, setPendingVisibilityChange] = useState<'friends_family' | 'public' | null>(null);
    
    // Settings state
    const [settings, setSettings] = useState<UserSettings>({
        displayName: '',
        email: '',
        bio: '',
        avatarUrl: '',
        phone: '',
        preferredContact: 'email',
        passportCountry: 'US',
        passportNumber: '',
        passportExpiry: '',
        tsaPrecheck: '',
        globalEntry: '',
        knownTravelerNumber: '',
        // Legal Name (as on ID/Passport)
        legalFirstName: '',
        legalMiddleName: '',
        legalLastName: '',
        dateOfBirth: '',
        gender: '',
        emergencyContact: {
            name: '',
            relationship: '',
            phone: '',
            email: '',
        },
        preferredAirline: '',
        frequentFlyerNumber: '',
        seatPreference: 'no_preference',
        mealPreference: '',
        specialAssistance: '',
        preferredHotel: '',
        hotelLoyaltyNumber: '',
        roomPreference: '',
        bedPreference: 'no_preference',
        floorPreference: 'no_preference',
        // Transportation
        preferredRideShare: '',
        uberAccountLinked: false,
        uberEmail: '',
        lyftAccountLinked: false,
        lyftEmail: '',
        preferredCarRental: '',
        carRentalLoyaltyNumber: '',
        preferredCarType: 'no_preference',
        hasTransitPass: false,
        transitPassType: '',
        preferredBikeShare: '',
        // Accessibility
        requiresWheelchair: false,
        requiresMobilityAssistance: false,
        requiresVisualAssistance: false,
        requiresHearingAssistance: false,
        hasServiceAnimal: false,
        serviceAnimalType: '',
        medicalDietaryNeeds: '',
        tsaCaresEnrolled: false,
        otherAccessibilityNeeds: '',
        // Special Status
        isStudent: false,
        studentEmail: '',
        studentIdNumber: '',
        isVeteran: false,
        veteranIdNumber: '',
        militaryBranch: '',
        isSenior: false,
        seniorAge: '',
        isAAAMember: false,
        aaaMemberNumber: '',
        isGovernmentEmployee: false,
        governmentAgency: '',
        isFirstResponder: false,
        firstResponderType: '',
        // Privacy
        defaultTripVisibility: 'private',
        profileVisibility: 'friends_family',
        showInCommunity: true,
        allowFriendRequests: true,
        autoSharePhotos: false,
        autoShareItinerary: false,
        autoShareDates: true,
        autoShareDestination: true,
        autoShareName: true,
        friendsFamilyAcknowledged: false,
        publicAcknowledged: false,
        emailNotifications: true,
        tripReminders: true,
        marketingEmails: false,
        friendActivity: true,
        creditAlerts: true,
    });

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const user = JSON.parse(storedUser);
                setSettings(prev => ({
                    ...prev,
                    displayName: user.name || user.email?.split('@')[0] || '',
                    email: user.email || '',
                }));
            } catch (e) {
                console.error('Error parsing user data:', e);
            }
        }
        
        // Get userId
        const storedUserForId = localStorage.getItem('user');
        if (storedUserForId) {
            try { const u = JSON.parse(storedUserForId); if (u.id || u.sub) setSettings(prev => ({ ...prev })); } catch {}
        }
        const savedSettings = localStorage.getItem('userSettings');
        if (savedSettings) {
            try {
                const parsed = JSON.parse(savedSettings);
                setSettings(prev => ({ ...prev, ...parsed }));
            } catch (e) {
                console.error('Error parsing settings:', e);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        const slug = SETTINGS_TABS[newValue];
        if (slug && typeof window !== 'undefined') {
            // Use replaceState (not assigning hash directly) so the URL bar
            // updates without scrolling and without polluting browser history
            // for every tab click. Back-button still works for the page-level
            // navigation that brought the user to /settings.
            window.history.replaceState(null, '', '#' + slug);
        }
    };

    // Sync tabValue when hash changes via browser back/forward, or via an
    // external link to e.g. /settings#account. Re-runs only on mount.
    useEffect(() => {
        const onHashChange = () => setTabValue(tabIndexFromHash());
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    const handleSettingChange = (field: keyof UserSettings, value: any) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    // Name visibility on public photos. Standalone control: persists directly to
    // the backend (/users/profile), NOT to the localStorage settings blob.
    const [showNameOnPublic, setShowNameOnPublic] = useState(false);
    const [showInCommunity, setShowInCommunity] = useState(true);
    const [communitySaving, setCommunitySaving] = useState(false);
    const [nameSaving, setNameSaving] = useState(false);

    const handleNameVisibilityToggle = async (next: boolean) => {
        const prev = showNameOnPublic;
        setNameSaving(true);
        setShowNameOnPublic(next); // optimistic
        try {
            const response = await api.put('/users/profile', { showNameOnPublic: next });
            const data = await response.json();
            if (response.ok && data.success) {
                setShowNameOnPublic(!!data.showNameOnPublic);
            } else {
                throw new Error(data.error || 'Failed to update');
            }
        } catch {
            setShowNameOnPublic(prev); // revert on failure - never lie about privacy state
        } finally {
            setNameSaving(false);
        }
    };

    const handleCommunityToggle = async (next: boolean) => {
        const prev = showInCommunity;
        setCommunitySaving(true);
        setShowInCommunity(next); // optimistic
        try {
            const response = await api.put('/users/profile', { showInCommunity: next });
            const data = await response.json();
            if (response.ok && data.success) {
                setShowInCommunity(data.showInCommunity !== false);
            } else {
                setShowInCommunity(prev); // revert on failure - never lie about privacy state
            }
        } catch {
            setShowInCommunity(prev); // revert on failure - never lie about privacy state
        } finally {
            setCommunitySaving(false);
        }
    };
    const handleEmergencyContactChange = (field: keyof EmergencyContact, value: string) => {
        setSettings(prev => ({
            ...prev,
            emergencyContact: { ...prev.emergencyContact, [field]: value }
        }));
    };

    const handleVisibilityChange = (newVisibility: 'private' | 'friends_family' | 'public') => {
        if (newVisibility === 'friends_family' && !settings.friendsFamilyAcknowledged) {
            setPendingVisibilityChange('friends_family');
            setFriendsFamilyDialogOpen(true);
        } else if (newVisibility === 'public' && !settings.publicAcknowledged) {
            setPendingVisibilityChange('public');
            setPublicDialogOpen(true);
        } else {
            handleSettingChange('defaultTripVisibility', newVisibility);
        }
    };

    const handleAcknowledgement = (type: 'friends_family' | 'public') => {
        if (type === 'friends_family') {
            setSettings(prev => ({
                ...prev,
                friendsFamilyAcknowledged: true,
                defaultTripVisibility: 'friends_family'
            }));
            setFriendsFamilyDialogOpen(false);
        } else {
            setSettings(prev => ({
                ...prev,
                publicAcknowledged: true,
                defaultTripVisibility: 'public'
            }));
            setPublicDialogOpen(false);
        }
        setPendingVisibilityChange(null);
    };

    const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setError('Image must be less than 5MB');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                handleSettingChange('avatarUrl', reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLinkUber = () => {
        // In production, this would open OAuth flow
        setSettings(prev => ({ ...prev, uberAccountLinked: true, uberEmail: prev.email }));
        setSaveSuccess(true);
    };

    const handleLinkLyft = () => {
        // In production, this would open OAuth flow
        setSettings(prev => ({ ...prev, lyftAccountLinked: true, lyftEmail: prev.email }));
        setSaveSuccess(true);
    };

    const handleSaveSettings = async () => {
        setLoading(true);
        setError('');
        
        try {
            localStorage.setItem('userSettings', JSON.stringify(settings));
            
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const user = JSON.parse(storedUser);
                user.name = settings.displayName;
                localStorage.setItem('user', JSON.stringify(user));
            }
            
            setSaveSuccess(true);
        } catch (err) {
            setError('Failed to save settings');
        } finally {
            setLoading(false);
        }
    };

    // ============ Notification Preferences (#230 Phase 4 - unified) ============
    // Single state, single load, single save. Backed by Phase 5.7 endpoint
    // /users/me/notification-preferences which handles all 17 fields per Patch 2.
    const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
    const [aiPrefs, setAiPrefs] = useState<AIPreferences | null>(null);
    const [aiPrefsLoading, setAiPrefsLoading] = useState(false);
    const [aiPrefsSaving, setAiPrefsSaving] = useState(false);
    const [aiPrefsError, setAiPrefsError] = useState('');
    const [prefsLoading, setPrefsLoading] = useState(false);
    const [prefsSaving, setPrefsSaving] = useState(false);
    const [prefsError, setPrefsError] = useState('');

    // Load current name-visibility setting from the backend.
    useEffect(() => {
        (async () => {
            try {
                const response = await api.get('/users/profile');
                const data = await response.json();
                if (response.ok && data.success && data.profile) {
                    setShowNameOnPublic(!!data.profile.showNameOnPublic);
                    setShowInCommunity(data.profile.showInCommunity !== false);
                }
            } catch { /* leave default (off) */ }
        })();
    }, []);

    useEffect(() => {
        const loadPrefs = async () => {
            setPrefsLoading(true);
            setPrefsError('');
            try {
                const response = await api.get('/users/me/notification-preferences');
                const data = await response.json();
                if (response.ok && data.success && data.preferences) {
                    setPrefs(data.preferences);
                } else {
                    throw new Error(data.error || 'Unknown error loading preferences');
                }
            } catch (err) {
                setPrefsError(err instanceof Error ? err.message : 'Failed to load notification preferences');
            } finally {
                setPrefsLoading(false);
            }
        };
        loadPrefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    // Load AI feature preferences (separate endpoint).
    useEffect(() => {
        const loadAiPrefs = async () => {
            setAiPrefsLoading(true);
            setAiPrefsError('');
            try {
                const response = await api.get('/users/me/ai-preferences');
                const data = await response.json();
                if (response.ok && data.success && data.preferences) {
                    setAiPrefs(data.preferences);
                } else {
                    throw new Error(data.error || 'Unknown error loading AI preferences');
                }
            } catch (err) {
                setAiPrefsError(err instanceof Error ? err.message : 'Failed to load AI preferences');
            } finally {
                setAiPrefsLoading(false);
            }
        };
        loadAiPrefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    // Generic single-field updater. Type-safe per K extends keyof NotificationPreferences.
    const updatePref = <K extends keyof NotificationPreferences>(
        key: K,
        value: NotificationPreferences[K]
    ) => {
        setPrefs(prev => (prev ? { ...prev, [key]: value } : prev));
    };

    // Toggle a single interval value in/out of the travelReminderIntervals array.
    const toggleInterval = (interval: number) => {
        setPrefs(prev => {
            if (!prev) return prev;
            const current = prev.travelReminderIntervals;
            const next = current.includes(interval)
                ? current.filter(n => n !== interval)
                : [...current, interval].sort((a, b) => a - b);
            return { ...prev, travelReminderIntervals: next };
        });
    };

    const savePrefs = async () => {
        if (!prefs) return;
        if (prefs.travelRemindersEnabled && prefs.travelReminderIntervals.length === 0) {
            setPrefsError('Pick at least one reminder interval, or turn off travel reminders.');
            return;
        }
        setPrefsSaving(true);
        setPrefsError('');
        try {
            const response = await api.put('/users/me/notification-preferences', prefs);
            const data = await response.json();
            if (response.ok && data.success && data.preferences) {
                setPrefs(data.preferences);
                setSaveSuccess(true);
            } else {
                throw new Error(data.error || 'Unknown error saving preferences');
            }
        } catch (err) {
            setPrefsError(err instanceof Error ? err.message : 'Failed to save notification preferences');
        } finally {
            setPrefsSaving(false);
        }
    };

    const updateAiPref = (key: keyof AIPreferences, value: boolean) => {
        setAiPrefs(prev => (prev ? { ...prev, [key]: value } : prev));
    };

    const saveAiPrefs = async () => {
        if (!aiPrefs) return;
        setAiPrefsSaving(true);
        setAiPrefsError('');
        try {
            const response = await api.put('/users/me/ai-preferences', aiPrefs);
            const data = await response.json();
            if (response.ok && data.success && data.preferences) {
                setAiPrefs(data.preferences);
                setSaveSuccess(true);
            } else {
                throw new Error(data.error || 'Unknown error saving AI preferences');
            }
        } catch (err) {
            setAiPrefsError(err instanceof Error ? err.message : 'Failed to save AI preferences');
        } finally {
            setAiPrefsSaving(false);
        }
    };

    const handlePasswordChange = async () => {
        setPasswordError('');
        
        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match');
            return;
        }
        
        if (newPassword.length < 8) {
            setPasswordError('Password must be at least 8 characters');
            return;
        }
        
        setPasswordLoading(true);
        
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            setPasswordDialogOpen(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setSaveSuccess(true);
        } catch (err) {
            setPasswordError('Failed to change password');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== 'DELETE') return;
        
        setDeleteLoading(true);
        
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            localStorage.clear();
            navigate('/');
        } catch (err) {
            setError('Failed to delete account');
        } finally {
            setDeleteLoading(false);
        }
    };

    const privacyOptions = [
        { value: 'private', label: 'Private', description: 'Only you can see', icon: <Lock sx={{ color: theme.palette.text.secondary }} /> },
        ...(friendsTripsEnabled ? [{ value: 'friends_family', label: 'Friends & Family', description: 'Shared with your circle', icon: <People sx={{ color: theme.palette.primary.main }} /> }] : []),
        ...(publicTripsEnabled ? [{ value: 'public', label: 'Public', description: 'Visible to everyone in community', icon: <Public sx={{ color: theme.palette.secondary.main }} /> }] : []),
    ];

    const countries = [
        { code: 'US', name: 'United States' }, { code: 'GB', name: 'United Kingdom' }, { code: 'CA', name: 'Canada' },
        { code: 'AU', name: 'Australia' }, { code: 'DE', name: 'Germany' }, { code: 'FR', name: 'France' },
        { code: 'IT', name: 'Italy' }, { code: 'ES', name: 'Spain' }, { code: 'JP', name: 'Japan' },
        { code: 'KR', name: 'South Korea' }, { code: 'CN', name: 'China' }, { code: 'IN', name: 'India' },
        { code: 'BR', name: 'Brazil' }, { code: 'MX', name: 'Mexico' }, { code: 'NG', name: 'Nigeria' },
        { code: 'KE', name: 'Kenya' }, { code: 'ZA', name: 'South Africa' }, { code: 'PH', name: 'Philippines' },
        { code: 'OTHER', name: 'Other' },
    ];

    const airlines = [
        'No Preference', 'American Airlines', 'Delta Air Lines', 'United Airlines', 'Southwest Airlines',
        'JetBlue Airways', 'Alaska Airlines', 'Spirit Airlines', 'Frontier Airlines', 'Hawaiian Airlines',
        'British Airways', 'Lufthansa', 'Air France', 'Emirates', 'Qatar Airways', 'Singapore Airlines',
        'Cathay Pacific', 'Japan Airlines', 'All Nippon Airways', 'Other',
    ];

    const hotelChains = [
        'No Preference', 'Marriott Bonvoy', 'Hilton Honors', 'IHG Rewards', 'World of Hyatt',
        'Wyndham Rewards', 'Best Western Rewards', 'Choice Privileges', 'Radisson Rewards',
        'Accor Live Limitless', 'Airbnb', 'VRBO', 'Other',
    ];

    const carRentalCompanies = [
        'No Preference', 'Enterprise', 'Hertz', 'Avis', 'Budget', 'National', 'Alamo',
        'Dollar', 'Thrifty', 'Sixt', 'Europcar', 'Turo', 'Zipcar', 'Other',
    ];

    const carTypes = [
        { value: 'no_preference', label: 'No Preference' },
        { value: 'economy', label: 'Economy' },
        { value: 'compact', label: 'Compact' },
        { value: 'midsize', label: 'Midsize' },
        { value: 'full_size', label: 'Full Size' },
        { value: 'suv', label: 'SUV' },
        { value: 'minivan', label: 'Minivan' },
        { value: 'luxury', label: 'Luxury' },
        { value: 'convertible', label: 'Convertible' },
        { value: 'electric', label: 'Electric/Hybrid' },
    ];

    const militaryBranches = [
        'Army', 'Navy', 'Air Force', 'Marine Corps', 'Coast Guard', 'Space Force', 'National Guard', 'Reserves',
    ];

    const firstResponderTypes = [
        'Police Officer', 'Firefighter', 'EMT/Paramedic', 'Nurse', 'Doctor', 'Dispatcher', 'Other',
    ];

    return (
        <Box sx={{ minHeight: '100vh', background: theme.palette.background.default }}>
            <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} accept="image/jpeg,image/png,image/gif" style={{ display: 'none' }} />

            {/* Header */}
            <AppBar position="sticky" color="transparent" elevation={0} sx={{ borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper', zIndex: 10 }}>
                <Toolbar>
                    <IconButton onClick={() => navigate('/trips')} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                        Settings
                    </Typography>
                    <Chip label="Early Access" size="small" variant="outlined" sx={{ borderColor: '#FF9800', color: '#E65100', fontWeight: 600, fontSize: '0.65rem', height: 22, mr: 1 }} />
                    <Chip label={tierLabel} size="small" sx={{ bgcolor: plan === 'pro' ? '#2D6E6A' : plan === 'premium' ? '#7B1FA2' : '#E8F5F3', color: plan === 'free' ? '#2D6E6A' : 'white', fontWeight: 700, fontSize: '0.75rem' }} />
                </Toolbar>
            </AppBar>

            <Container maxWidth="sm" sx={{ py: 3, pb: 8 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

                {/* Section: Account */}
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, px: 1 }}>Account</Typography>
                <Card sx={{ borderRadius: '16px', mb: 2, mt: 0.5 }}>
                    {/* Profile */}
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 2, cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                        onClick={() => setActiveSection(activeSection === 'profile' ? null : 'profile')}>
                        <Avatar src={settings.avatarUrl} sx={{ width: 44, height: 44, bgcolor: '#2D6E6A', mr: 2, fontSize: '1.1rem' }}>
                            {settings.displayName?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 600 }}>{settings.displayName || 'Your Name'}</Typography>
                            <Typography variant="caption" color="text.secondary">{settings.email}</Typography>
                        </Box>
                        <Person sx={{ color: 'text.secondary' }} />
                    </Box>
                    {activeSection === 'profile' && (
                        <Box sx={{ px: 2, pb: 2, borderTop: '1px solid #F0F0F0' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                <Box sx={{ position: 'relative' }}>
                                    <Avatar src={settings.avatarUrl} sx={{ width: 80, height: 80, bgcolor: '#2D6E6A', fontSize: '2rem' }}>
                                        {settings.displayName?.charAt(0)?.toUpperCase() || 'U'}
                                    </Avatar>
                                    <IconButton size="small" onClick={() => fileInputRef.current?.click()}
                                        sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: 'white', border: '1px solid #E0E0E0', width: 28, height: 28 }}>
                                        <PhotoCamera sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Box>
                            </Box>
                            <TextField fullWidth size="small" label="Display Name" value={settings.displayName} onChange={(e) => handleSettingChange('displayName', e.target.value)} sx={{ mb: 1.5 }} />
                            <TextField fullWidth size="small" label="Email" value={settings.email} disabled helperText="Email cannot be changed" sx={{ mb: 1.5 }} />
                            <TextField fullWidth size="small" label="Phone" value={settings.phone} onChange={(e) => handleSettingChange('phone', e.target.value)} sx={{ mb: 1.5 }} />
                            <TextField fullWidth size="small" label="Bio" value={settings.bio} onChange={(e) => handleSettingChange('bio', e.target.value)} multiline rows={2} sx={{ mb: 1.5 }} />
                            <Button variant="contained" fullWidth onClick={handleSaveSettings} disabled={loading}
                                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' }, borderRadius: '12px', textTransform: 'none' }}>
                                {loading ? 'Saving...' : 'Save Profile'}
                            </Button>
                        </Box>
                    )}
                    <Divider />
                    {/* Change Password */}
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                        onClick={() => setPasswordDialogOpen(true)}>
                        <Lock sx={{ color: 'text.secondary', mr: 2 }} />
                        <Typography sx={{ flex: 1 }}>Change Password</Typography>
                        <Security sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </Box>
                    <Divider />
                    {/* Notifications */}
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                        onClick={() => setActiveSection(activeSection === 'notifications' ? null : 'notifications')}>
                        <Notifications sx={{ color: 'text.secondary', mr: 2 }} />
                        <Typography sx={{ flex: 1 }}>Notifications</Typography>
                        <Person sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </Box>
                    {activeSection === 'notifications' && prefs && (
                        <Box sx={{ px: 2, pb: 2, borderTop: '1px solid #F0F0F0' }}>
                            {prefsError && <Alert severity="error" sx={{ mb: 1, mt: 1 }}>{prefsError}</Alert>}
                            <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={prefs.appNudgesEnabled} onChange={(e) => updatePref('appNudgesEnabled', e.target.checked)} color="primary" />}
                                label={<Typography variant="body2">In-app notifications</Typography>} />
                            <FormControlLabel control={<Switch checked={prefs.emailEnabled} onChange={(e) => updatePref('emailEnabled', e.target.checked)} color="primary" />}
                                label={<Typography variant="body2">Email notifications</Typography>} />
                            <FormControlLabel control={<Switch checked={prefs.travelRemindersEnabled} onChange={(e) => updatePref('travelRemindersEnabled', e.target.checked)} color="primary" />}
                                label={<Typography variant="body2">Travel reminders</Typography>} />
                            <FormControlLabel control={<Switch checked={prefs.featureAnnouncementsEnabled} onChange={(e) => updatePref('featureAnnouncementsEnabled', e.target.checked)} color="primary" />}
                                label={<Typography variant="body2">Feature announcements</Typography>} />
                            <FormControlLabel control={<Switch checked={prefs.marketingEnabled} onChange={(e) => updatePref('marketingEnabled', e.target.checked)} color="primary" />}
                                label={<Typography variant="body2">Tips &amp; promotions</Typography>} />
                            <Button size="small" variant="contained" onClick={savePrefs} disabled={prefsSaving}
                                sx={{ mt: 1, bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' }, borderRadius: '12px', textTransform: 'none' }}>
                                {prefsSaving ? 'Saving...' : 'Save'}
                            </Button>
                        </Box>
                    )}
                </Card>

                {/* Section: Friends */}
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, px: 1 }}>Friends</Typography>
                <Card sx={{ borderRadius: '16px', mb: 2, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                        onClick={() => navigate('/friends?from=settings')}>
                        <People sx={{ color: 'text.secondary', mr: 2 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography>My Friends</Typography>
                            <Typography variant="caption" color="text.secondary">Manage friends, invitations and connections</Typography>
                        </Box>
                        <Person sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </Box>
                </Card>

                {/* Section: Privacy & Visibility */}
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, px: 1 }}>Privacy & Visibility</Typography>
                <Card sx={{ borderRadius: '16px', mb: 2, mt: 0.5 }}>
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Default Trip Visibility</Typography>
                        <RadioGroup value={settings.defaultTripVisibility} onChange={(e) => handleVisibilityChange(e.target.value as any)}>
                            {privacyOptions.map((option) => (
                                <FormControlLabel key={option.value} value={option.value} control={<Radio size="small" sx={{ color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' } }} />}
                                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{option.icon}<Typography variant="body2">{option.label}</Typography></Box>} />
                            ))}
                        </RadioGroup>
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <FormControlLabel control={<Switch checked={settings.allowFriendRequests} onChange={(e) => handleSettingChange('allowFriendRequests', e.target.checked)} color="primary" />}
                            label={<Typography variant="body2">Allow friend requests</Typography>} />
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <FormControlLabel control={<Switch checked={showInCommunity} onChange={(e) => handleCommunityToggle(e.target.checked)} disabled={communitySaving} color="primary" />}
                            label={<Typography variant="body2">Show my trips in Community</Typography>} />
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <FormControlLabel control={<Switch checked={showNameOnPublic} onChange={(e) => handleNameVisibilityToggle(e.target.checked)} disabled={nameSaving} color="primary" />}
                            label={<Typography variant="body2">Show my name on public photos</Typography>} />
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
                            When off, your photos appear without your name. Saved automatically.
                        </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1 }}>
                        <Button size="small" variant="contained" onClick={handleSaveSettings} disabled={loading}
                            sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' }, borderRadius: '12px', textTransform: 'none' }}>
                            Save
                        </Button>
                    </Box>
                </Card>

                {/* Section: AI Features */}
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, px: 1 }}>AI Features</Typography>
                <Accordion disableGutters sx={{ borderRadius: '16px', mb: 2, mt: 0.5, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', '&:before': { display: 'none' }, overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<Typography sx={{ fontSize: 18, color: 'text.secondary' }}>▾</Typography>} sx={{ px: 2 }}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>AI Features</Typography>
                                <IconButton size="small" aria-label="About AI features and data use" onClick={(e) => { e.stopPropagation(); navigate('/privacy'); }} sx={{ p: 0.25 }}>
                                    <Typography component="span" sx={{ fontSize: 15, lineHeight: 1, color: 'text.secondary' }}>ⓘ</Typography>
                                </IconButton>
                            </Box>
                            <Typography variant="caption" color="text.secondary">Trip features are on by default. Turn off anything you’d rather not use.</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pt: 0 }}>
                        {aiPrefsError && <Alert severity="error" sx={{ mb: 1 }}>{aiPrefsError}</Alert>}
                        {aiPrefsLoading ? (
                            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Box>
                        ) : aiPrefs ? (
                            <>
                                <FormControlLabel control={<Switch checked={aiPrefs.trip_planner} onChange={(e) => updateAiPref('trip_planner', e.target.checked)} color="primary" />}
                                    label={<Typography variant="body2">Trip planning</Typography>} />
                                {!aiPrefs.trip_planner && <Alert severity="warning" sx={{ ml: 4, mb: 1, py: 0, fontSize: 12 }}>Trip planning is off — AI won’t suggest itineraries or trip ideas.</Alert>}
                                <FormControlLabel control={<Switch checked={aiPrefs.activity_recommendations} onChange={(e) => updateAiPref('activity_recommendations', e.target.checked)} color="primary" />}
                                    label={<Typography variant="body2">Activity suggestions</Typography>} />
                                {!aiPrefs.activity_recommendations && <Alert severity="warning" sx={{ ml: 4, mb: 1, py: 0, fontSize: 12 }}>Activity suggestions are off — Percoro won’t recommend things to do.</Alert>}
                                <FormControlLabel control={<Switch checked={aiPrefs.trip_import} onChange={(e) => updateAiPref('trip_import', e.target.checked)} color="primary" />}
                                    label={<Typography variant="body2">PDF trip import</Typography>} />
                                {!aiPrefs.trip_import && <Alert severity="warning" sx={{ ml: 4, mb: 1, py: 0, fontSize: 12 }}>PDF import is off — uploaded booking PDFs won’t auto-fill trip details.</Alert>}
                                <FormControlLabel control={<Switch checked={aiPrefs.photo_analysis} onChange={(e) => updateAiPref('photo_analysis', e.target.checked)} color="primary" />}
                                    label={<Typography variant="body2">Photo analysis</Typography>} />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5, mb: 1 }}>AI suggests captions, tags &amp; a location guess for photos you upload.</Typography>
                                {aiPrefs.photo_analysis && <Alert severity="info" sx={{ ml: 4, mb: 1, py: 0, fontSize: 12 }}>Photos you upload are sent to third-party AI providers (e.g. Anthropic, AWS) to generate this. They process your photos under their own terms, which Percoro doesn’t control. Don’t enable this for photos you don’t want processed externally. <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate('/privacy')}>Privacy Policy</span></Alert>}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                                    <Button size="small" variant="contained" onClick={saveAiPrefs} disabled={aiPrefsSaving}
                                        sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' }, borderRadius: '12px', textTransform: 'none' }}>
                                        {aiPrefsSaving ? 'Saving...' : 'Save'}
                                    </Button>
                                </Box>
                            </>
                        ) : null}
                    </AccordionDetails>
                </Accordion>

                {/* Section: Booking Preferences (hidden behind flag) */}
                {bookingEnabled && (
                    <>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, px: 1 }}>Booking Preferences</Typography>
                        <Card sx={{ borderRadius: '16px', mb: 2, mt: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                                onClick={() => setTabValue(2)}>
                                <Flight sx={{ color: 'text.secondary', mr: 2 }} />
                                <Typography sx={{ flex: 1 }}>Flight Preferences</Typography>
                                <Person sx={{ color: 'text.secondary', fontSize: 18 }} />
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                                onClick={() => setTabValue(3)}>
                                <Hotel sx={{ color: 'text.secondary', mr: 2 }} />
                                <Typography sx={{ flex: 1 }}>Hotel Preferences</Typography>
                                <Person sx={{ color: 'text.secondary', fontSize: 18 }} />
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                                onClick={() => setTabValue(4)}>
                                <DirectionsCar sx={{ color: 'text.secondary', mr: 2 }} />
                                <Typography sx={{ flex: 1 }}>Transportation Preferences</Typography>
                                <Person sx={{ color: 'text.secondary', fontSize: 18 }} />
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                                onClick={() => setTabValue(5)}>
                                <Accessible sx={{ color: 'text.secondary', mr: 2 }} />
                                <Typography sx={{ flex: 1 }}>Accessibility & Benefits</Typography>
                                <Person sx={{ color: 'text.secondary', fontSize: 18 }} />
                            </Box>
                        </Card>
                    </>
                )}

                {/* Section: Plan & Storage */}
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, px: 1 }}>Plan & Storage</Typography>
                <Card sx={{ borderRadius: '16px', mb: 2, mt: 0.5 }}>
                    <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontWeight: 600 }}>Current Plan</Typography>
                            <Typography variant="caption" color="text.secondary">{tierDescription}</Typography>
                        </Box>
                        <Chip label={tierLabel} size="small" sx={{ bgcolor: plan === 'pro' ? '#2D6E6A' : plan === 'premium' ? '#7B1FA2' : '#E8F5F3', color: plan === 'free' ? '#2D6E6A' : 'white', fontWeight: 700 }} />
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary" variant="body2" sx={{ cursor: 'pointer' }} onClick={() => navigate('/plans')}>{plan === 'free' ? 'Upgrade Plan' : 'View Plans'}</Typography>
                        <Chip label={plan === 'free' ? 'View Plans' : tierLabel} size="small" onClick={() => navigate('/plans')} sx={{ cursor: 'pointer', bgcolor: plan === 'free' ? '#E8F5F3' : '#2D6E6A', color: plan === 'free' ? '#2D6E6A' : 'white', fontWeight: 600 }} />
                    </Box>
                </Card>

                {/* Section: Support */}
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, px: 1 }}>Support</Typography>
                <Card sx={{ borderRadius: '16px', mb: 2, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                        onClick={() => setActiveSection(activeSection === 'support' ? null : 'support')}>
                        <SupportAgent sx={{ color: 'text.secondary', mr: 2 }} />
                        <Typography sx={{ flex: 1 }}>Contact Us</Typography>
                        <Person sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </Box>
                    {activeSection === 'support' && (
                        <Box sx={{ borderTop: '1px solid #F0F0F0', px: 2, py: 2 }}>
                            <SupportSettingsPanel />
                        </Box>
                    )}
                    <Divider />
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                        onClick={() => { navigator.share?.({ title: 'Percoro', text: 'Share your travels with Percoro!', url: 'https://percoro.com' }); }}>
                        <People sx={{ color: 'text.secondary', mr: 2 }} />
                        <Typography sx={{ flex: 1 }}>Share Percoro with Friends</Typography>
                        <Person sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </Box>
                </Card>

                {/* Section: Account Actions */}
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, px: 1 }}>Account</Typography>
                <Card sx={{ borderRadius: '16px', mb: 2, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#FFF5F5' } }}
                        onClick={() => setDeleteDialogOpen(true)}>
                        <Delete sx={{ color: 'error.main', mr: 2 }} />
                        <Typography sx={{ flex: 1, color: 'error.main' }}>Delete Account</Typography>
                    </Box>
                </Card>

                {/* Section: Info & Legal */}
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, px: 1 }}>Info & Legal</Typography>
                <Card sx={{ borderRadius: '16px', mb: 2, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                        onClick={() => window.location.assign('/privacy')}>
                        <Lock sx={{ color: 'text.secondary', mr: 2 }} />
                        <Typography sx={{ flex: 1 }}>Privacy Policy</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                        onClick={() => window.location.assign('/terms')}>
                        <Security sx={{ color: 'text.secondary', mr: 2 }} />
                        <Typography sx={{ flex: 1 }}>Terms of Service</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">App Version: 1.0.0</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>User ID</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#666', wordBreak: 'break-all', flex: 1 }}>
                                {(() => { try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.id || u.sub || u.userId || ''; } catch { return ''; } })()}
                            </Typography>
                            <IconButton size="small" onClick={() => {
                                const uid = (() => { try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.id || u.sub || u.userId || ''; } catch { return ''; } })();
                                navigator.clipboard.writeText(uid);
                                setIdCopied(true);
                                setTimeout(() => setIdCopied(false), 2000);
                            }} sx={{ color: idCopied ? '#2D6E6A' : 'text.secondary', flexShrink: 0 }}>
                                {idCopied ? <Save fontSize="small" /> : <PhotoCamera fontSize="small" />}
                            </IconButton>
                        </Box>
                    </Box>
                </Card>
            </Container>

            {/* Friends & Family Acknowledgement Dialog */}
            <Dialog open={friendsFamilyDialogOpen} onClose={() => setFriendsFamilyDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <People color="primary" /> Friends & Family Sharing
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>Please read and acknowledge the following:</Alert>
                    <Typography variant="body1" sx={{ mb: 2 }}>By selecting <strong>Friends & Family</strong>, you understand that:</Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                        <li><Typography variant="body2">Your trips will be visible to users you've added as friends or family</Typography></li>
                        <li><Typography variant="body2">You can manage your friends list and remove access at any time</Typography></li>
                        <li><Typography variant="body2">You can override visibility settings for individual trips</Typography></li>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setFriendsFamilyDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => handleAcknowledgement('friends_family')}>I Understand & Agree</Button>
                </DialogActions>
            </Dialog>

            {/* Public Acknowledgement Dialog */}
            <Dialog open={publicDialogOpen} onClose={() => setPublicDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.warning.dark }}>
                    <Warning color="warning" /> Public Sharing Warning
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>Please read carefully before proceeding:</Alert>
                    <Typography variant="body1" sx={{ mb: 2 }}>By selecting <strong>Public</strong>, you understand that:</Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                        <li><Typography variant="body2"><strong>Anyone on the internet</strong> can view your public trips</Typography></li>
                        <li><Typography variant="body2">You should <strong>never share sensitive information</strong> publicly</Typography></li>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setPublicDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={() => handleAcknowledgement('public')}>I Understand & Agree</Button>
                </DialogActions>
            </Dialog>

            {/* Password Change Dialog */}
            <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Change Password</DialogTitle>
                <DialogContent>
                    {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
                    <TextField fullWidth label="Current Password" type={showCurrentPassword ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} sx={{ mb: 2, mt: 1 }}
                        InputProps={{ endAdornment: (<IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)}>{showCurrentPassword ? <VisibilityOff /> : <Visibility />}</IconButton>) }} />
                    <TextField fullWidth label="New Password" type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} sx={{ mb: 2 }}
                        InputProps={{ endAdornment: (<IconButton onClick={() => setShowNewPassword(!showNewPassword)}>{showNewPassword ? <VisibilityOff /> : <Visibility />}</IconButton>) }} />
                    <TextField fullWidth label="Confirm New Password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handlePasswordChange} disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}>
                        {passwordLoading ? <CircularProgress size={20} /> : 'Change Password'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Account Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', color: theme.palette.error.main, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning /> Delete Account
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Your account will be <strong>scheduled for deletion</strong>. You have 30 days to recover it.
                    </Alert>
                    <Typography variant="body1" sx={{ mb: 1 }}>Type <strong>DELETE</strong> to confirm:</Typography>
                    <TextField fullWidth value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder="Type DELETE" autoComplete="off" />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteAccount} disabled={deleteLoading || deleteConfirmText !== 'DELETE'}>
                        {deleteLoading ? <CircularProgress size={20} /> : 'Delete My Account'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar open={saveSuccess} autoHideDuration={3000} onClose={() => setSaveSuccess(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="success" onClose={() => setSaveSuccess(false)}>Settings saved successfully!</Alert>
            </Snackbar>
        </Box>
    );
};

export default SettingsPage;






